import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { wasReadOnlyRefusedRecently } from '@personal-trainer-app/shared';
import {
  registerSessionScoped,
  unregisterSessionScoped,
} from '@personal-trainer-app/shared/src/store/session-scope';

import { Toast } from './Toast';

import type {
  ToastContextValue,
  ToastData,
  ToastCreateData,
  ToastOptions,
  ToastProviderProps,
  ToastPosition,
} from './Toast.types';

/**
 * Generate unique toast ID
 */
let toastId = 0;
function generateId(): string {
  return `toast-${++toastId}`;
}

/**
 * Toast Context
 */
const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Position styles for the toast container
 */
const positionStyles: Record<ToastPosition, string> = {
  'top-left': 'top-0 left-0 items-start',
  'top-center': 'top-0 left-1/2 -translate-x-1/2 items-center',
  'top-right': 'top-0 right-0 items-end',
  'bottom-left': 'bottom-0 left-0 items-start',
  'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2 items-center',
  'bottom-right': 'bottom-0 right-0 items-end',
};

/**
 * ToastProvider component for managing toast notifications
 *
 * @example
 * ```tsx
 * // Wrap your app with ToastProvider
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // Then use the useToast hook in any component
 * function MyComponent() {
 *   const { success, error } = useToast();
 *
 *   return (
 *     <button onClick={() => success('Saved!')}>
 *       Save
 *     </button>
 *   );
 * }
 * ```
 */
export function ToastProvider({
  children,
  defaultPosition = 'top-right',
  defaultDuration = 4000,
  maxToasts = 5,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  // Add a new toast
  const addToast = useCallback(
    (toast: ToastCreateData): string => {
      // A request was just refused as read-only (an admin signed in as the
      // member): the read-only notice says so in the server's words, so a
      // page's own "Could not save…" stands down rather than sit under it.
      if (
        'variant' in toast &&
        toast.variant === 'error' &&
        wasReadOnlyRefusedRecently()
      ) {
        return '';
      }

      const id = generateId();
      const duration = toast.duration ?? defaultDuration;
      // A rendered toast owns its own dismiss; only a standard one is drawn
      // with the provider's.
      const newToast: ToastData = toast.render
        ? { ...toast, id, duration }
        : {
            ...toast,
            id,
            duration,
            dismissible: toast.dismissible ?? true,
          };

      setToasts((prev) => {
        // Remove oldest toasts if we're at max
        const updated = [...prev, newToast];
        if (updated.length > maxToasts) {
          return updated.slice(-maxToasts);
        }
        return updated;
      });

      return id;
    },
    [defaultDuration, maxToasts]
  );

  // Remove a toast by id
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Remove all toasts
  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // A toast is the session's: its text and its action belong to whoever was
  // signed in when it was raised. The provider outlives the signed-in layout
  // (it is mounted above the router), so it drops them itself at the session
  // boundary rather than letting one linger onto the login page, or into the
  // next account's first seconds.
  useEffect(() => {
    registerSessionScoped('toasts', removeAllToasts);
    return () => unregisterSessionScoped('toasts');
  }, [removeAllToasts]);

  // Convenience methods
  const toast = useCallback(
    (message: ReactNode, options?: ToastOptions): string => {
      return addToast({ message, variant: 'default', ...options });
    },
    [addToast]
  );

  const success = useCallback(
    (message: ReactNode, options?: Omit<ToastOptions, 'variant'>): string => {
      return addToast({ message, variant: 'success', ...options });
    },
    [addToast]
  );

  const error = useCallback(
    (message: ReactNode, options?: Omit<ToastOptions, 'variant'>): string => {
      return addToast({ message, variant: 'error', ...options });
    },
    [addToast]
  );

  const warning = useCallback(
    (message: ReactNode, options?: Omit<ToastOptions, 'variant'>): string => {
      return addToast({ message, variant: 'warning', ...options });
    },
    [addToast]
  );

  const info = useCallback(
    (message: ReactNode, options?: Omit<ToastOptions, 'variant'>): string => {
      return addToast({ message, variant: 'info', ...options });
    },
    [addToast]
  );

  // Context value
  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      addToast,
      removeToast,
      removeAllToasts,
      toast,
      success,
      error,
      warning,
      info,
    }),
    [
      toasts,
      addToast,
      removeToast,
      removeAllToasts,
      toast,
      success,
      error,
      warning,
      info,
    ]
  );

  // Responsive position: top-center on mobile, defaultPosition on larger screens
  const responsivePositionClasses = `
    ${positionStyles['top-center']}
    sm:${positionStyles[defaultPosition].replace(/ /g, ' sm:')}
  `;

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container */}
      {toasts.length > 0 && (
        <div
          data-testid="toast-container"
          className={`
            fixed z-50 flex flex-col gap-2 p-4
            pointer-events-none
            ${responsivePositionClasses}
          `}
          style={{ maxHeight: 'calc(100vh - 32px)' }}
        >
          {/* Mobile: top-center positioning */}
          <div className="sm:hidden absolute inset-0 flex flex-col gap-2 p-4 items-center top-0" />

          {toasts.map((toastData) => (
            <div key={toastData.id} className="pointer-events-auto">
              {toastData.render ? (
                toastData.render({
                  remove: () => removeToast(toastData.id),
                  duration: toastData.duration ?? defaultDuration,
                })
              ) : (
                <Toast {...toastData} onRemove={removeToast} />
              )}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

/**
 * Hook to access toast functionality
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { success, error, toast } = useToast();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       success('Data saved successfully!');
 *     } catch (err) {
 *       error('Failed to save data');
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * }
 * ```
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
}

export default ToastProvider;
